import { useEffect, useState } from 'react';
import styled from 'styled-components';
import background1 from '../../src/assets/images/landing/background1.jpg';
import background2 from '../../src/assets/images/landing/background2.jpg';
import tweet1 from '../../src/assets/images/landing/tweet1.jpg';
import tweet2 from '../../src/assets/images/landing/tweet2.png';
import chatImage from '../../src/assets/images/landing/chat-image.png';
import impressionsIcon from '../../src/assets/images/landing/impressions-icon.png';
import platformsIcon from '../../src/assets/images/landing/platforms-icon.png';
import costIcon from '../../src/assets/images/landing/cost-icon.png';
import usersIcon from '../../src/assets/images/landing/users-icon.png';
import { ChatBubbleOutlineRounded, AccountCircleOutlined, SettingsOutlined, TroubleshootOutlined } from '@mui/icons-material';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import client from '../utils/contentfulClient';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { renderOptions } from '../utils/contentfulClient';
import Spinner from '../components/Spinner';

const LandingWrapper = styled.div`
    background-color: #101F3F;
    position: relative;
    min-height: 100vh;
    
    * {
        box-sizing: border-box;
        color: white;
        font-family: 'Montserrat', sans-serif;
    }
`

const HeroSection = styled.section`
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: url(${background1}) no-repeat center center/cover;
    padding: 4rem 2rem;
    min-height: 98vh;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(84, 147, 183, 0.4);
        z-index: 1;
    }
`;

const ContentWrapper = styled.div`
    max-width: 1200px;
    margin: auto;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    z-index: 2;
    position: relative;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }
`;

const Left = styled.div`
    flex: 1;
    padding: 1rem;

    @media (max-width: 768px) {
        padding: 1rem 0;
    }
`;

const Right = styled.div`
    flex: 0.9;
    padding: 1rem;

    @media (max-width: 768px) {
        padding: 1rem 0;
    }
`;

const Headline = styled.h1`
    font-size: 3.75rem;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 1rem;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SubHeadline = styled.div`
    p {
        font-size: 1.4rem;
        line-height: 1.5;
    }

    @media (max-width: 768px) {
        p {
            font-size: 1rem;
        }
    }
`;

const CTAButton = styled.a`
    display: block;
    background-color: #0f172a;
    padding: 1.5rem 0;
    border-radius: 1.4rem;
    border: none;
    font-size: 1.4rem;
    font-weight: bold;
    margin-top: 1rem;
    text-decoration: none;
    text-align: center;
    transition: background 0.3s ease;
    color: white;
    cursor: pointer;

    &:hover {
        background-color: #1e293b;
    }

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const PoweredBy = styled.p`
    font-size: 0.9rem;

    @media (max-width: 768px) {
        position: static;
        margin-top: 2rem;
        text-align: center;
    }
`;

const Section = styled.section`
    background-color: #101F3F;
    border-radius: 20px 20px 0 0;
    margin-top: -30px;
    padding: 5rem 2rem;
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
        flex-wrap: nowrap;
        padding: 0;
    }
`;

const TextBox = styled.div`
    background: linear-gradient(135deg, #101F3F, #71798D);
    border: 2px solid white;
    border-radius: 20px;
    padding: 2rem;
    max-width: 500px;
    flex: 1;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);
    margin: 1rem;

    @media (max-width: 768px) {
        max-width: 100%;
        text-align: center;
    }
`;

const Title = styled.h2`
    font-size: 1.8rem;
    font-weight: 800;
    margin-bottom: 1rem;
`;

const TextContainer = styled.div`
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1rem;
`;

const ImageContainer = styled.div`
    flex: 1;
    max-width: 600px;
    margin: 1rem;
    display: flex;
    justify-content: center;

    img {
        width: 100%;
        max-width: 500px;
        height: auto;
    }

    @media (max-width: 768px) {
        margin-top: 2rem;
    }
`;

const GradientWrapper = styled.div`
    background: radial-gradient(circle at left center, #508DB0 35%,#508DB0 35%,#101f3f 65%);
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    padding: 5rem 0 5rem 3rem;

    @media (max-width: 768px) {
        padding: 0;
        width: 100%;
    }
`

const Card = styled.div`
    background: transparent;
    backdrop-filter: blur(12px);
    border-top: 2px solid white;
    border-left: 2px solid white;
    border-bottom: 2px solid white;
    border-right: none;
    border-radius: 24px 0 0 24px;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.2);
    flex: 1.2 1.2 450px;
    padding: 2.5rem 2rem;

    @media (max-width: 768px) {
        margin-left: 1rem;
    }
`;

const Feature = styled.div`
  margin-bottom: 2rem;
`;

const IconWrapper = styled.div`
  font-size: 1.8rem;
  flex-shrink: 0;
  margin-top: 3px;
`;

const Text = styled.div`
  font-size: 1.125rem;
  line-height: 1.6;
`;

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    @media (min-width: 768px) {
        margin: 80px 0 0 0;
    }
`

const TextSection = styled.div`
  flex: 1 1 400px;
  padding: 3rem;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 1.1rem;

  &::before {
    content: '🟢';
    margin-right: 0.75rem;
    font-size: 1rem;
  }
`;

const TweetSection = styled.div`
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    margin: 0 auto;

    @media (min-width: 768px) {
        margin: 0 6rem 0 0;
    }
`;

const TweetImage = styled.img`
  width: 300px;
  border-radius: 12px;
  box-shadow: 0 0 15px rgba(0,0,0,0.3);
  background: black;
`;

const Quote = styled.blockquote`
  margin: 1rem auto;
  font-style: italic;
  font-size: 1.1rem;
  text-align: right;
  color: #E3E9F5;
  width: 88%;

  @media (min-width: 768px) {
    margin-top: 3rem;
    margin-bottom: 5rem;
  }
`;

const StatsSection = styled.section`
  background-color: #101F3F;
  color: white;
  text-align: center;
  padding: 2rem 1rem;

  @media (min-width: 768px) {
    padding: 1rem 3rem 6rem 3rem;
  }
`;

const StatsTitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

// Description
const StatsDescription = styled.p`
  max-width: 750px;
  margin: 0 auto 2rem;
  font-size: 1.05rem;
  line-height: 1.6;

  &:last-of-type {
    margin-bottom: 3rem;
  }
`;

const StatsContainer = styled.div`
    display: grid;
    gap: 2rem;

    @media (min-width: 768px) {
        grid-template-columns: repeat(4, 1fr);
        gap: 3rem;
        max-width: 1000px;
        margin: 0 auto;
    }
`;

// Individual stat block
const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  img {
    width: 50px;
    height: auto;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.4;
  }
`;

const SectionWrapper = styled.section`
  position: relative;
  background: url(${background2}) no-repeat center center/cover;
  color: white;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 5rem 0;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(16, 31, 63, 0.7);
    z-index: 0;
  }
`;

const CardWrapper = styled.div`
    position: relative;
    z-index: 1;
    margin: 0 0 0 10%;
    max-width: 700px;
    width: 70%;

    @media (max-width: 768px) {
        margin: 0 auto;
        width: 80%;
    }
`;

// 🆕 New InfoCard component for the blue card
const InfoCard = styled.div`
  background-color: #528fb2;
  width: 100%;
  z-index: 1;
  border-radius: 4px;
  padding: 15px 4rem 3rem 4rem;
  box-shadow: 0px 4px 15px rgba(0,0,0,0.25);
`;

const CardButton = styled.a`
  background-color: white;
  color: #0b1e3e;
  display: block;
  font-weight: 700;
  border: none;
  padding: 0.8rem 1.6rem;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  cursor: pointer;
  position: absolute;
  right: 0;
  bottom: -45px;
  width: 250px;
  z-index: 1;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const CardTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #0b1e3e;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`

const LandingPage = () => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        client
        .getEntries({ content_type: 'landingPage' })
        .then((response) => {
            if (response.items.length > 0) {
                setContent(response.items[0].fields);
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error('Error fetching content from Contentful:', error);
            setLoading(false);
        });
    }, [])

    return (
        <LandingWrapper>
            <NavBar />
            {loading ? (
                <LoadingWrapper>
                    <Spinner />
                </LoadingWrapper>
            ) : (
                <>
                    <HeroSection>
                        <ContentWrapper>
                            <Left>
                                <Headline>
                                    {content.heroTitle}
                                </Headline>
                                <PoweredBy>
                                    A project by <a href='https://www.civichealthproject.org/' target="_blank" rel='noopener noreferrer'>Civic Health Project</a>
                                </PoweredBy>
                            </Left>
                            <Right>
                                <SubHeadline>
                                    {documentToReactComponents(content.heroDescription, renderOptions)}
                                </SubHeadline>
                                <CTAButton target="_blank" rel='noopener noreferrer' href='https://docs.google.com/forms/d/e/1FAIpQLSeYLVpjVbJ9GZpd-WktH5agYZ4yERHuZgbGFZ4hAIHiy-_Cmw/viewform?usp=sharing'>
                                    Apply to join the Normsy network
                                </CTAButton>
                            </Right>
                        </ContentWrapper>
                    </HeroSection>
                    <Section>
                        <TextBox>
                            <Title>
                                {content.secondTitle}
                            </Title>
                            <TextContainer>
                                {documentToReactComponents(content.secondDescription, renderOptions)}
                            </TextContainer>
                        </TextBox>
                        <ImageContainer>
                            <img src={chatImage} alt="Normsy.ai" />
                        </ImageContainer>
                    </Section>
                    <GradientWrapper>
                        <TextSection>
                            <Title>
                                {content.thirdTitle}
                            </Title>
                            <TextContainer>
                                {documentToReactComponents(content.thirdDescription, renderOptions)}
                            </TextContainer>
                        </TextSection>
                        <Card>
                            <Feature>
                                <IconWrapper><ChatBubbleOutlineRounded fontSize="large" /></IconWrapper>
                                <Text>Spots high impact conversations that threaten civic norms</Text>
                            </Feature>
                            <Feature>
                                <IconWrapper><AccountCircleOutlined fontSize="large" /></IconWrapper>
                                <Text>Generates smart, respectful, evidence-based replies</Text>
                            </Feature>
                            <Feature>
                                <IconWrapper><SettingsOutlined fontSize="large" /></IconWrapper>
                                <Text>Lets users choose and customize what to say</Text>
                            </Feature>
                            <Feature>
                                <IconWrapper><TroubleshootOutlined fontSize="large" /></IconWrapper>
                                <Text>Tracks how interventions perform over time</Text>
                            </Feature>
                        </Card>
                        <Container>
                            <TextSection>
                                <Title>
                                    {content.fourthTitle}
                                </Title>
                                <TextContainer>
                                    {documentToReactComponents(content.fourthDescription, renderOptions)}
                                </TextContainer>
                                <BulletList>
                                    <BulletItem>Language that models mutual respect</BulletItem>
                                    <BulletItem>Messages that reinforce institutional trust</BulletItem>
                                    <BulletItem>Strategies that highlight shared democratic values</BulletItem>
                                </BulletList>
                            </TextSection>
                            <TweetSection>
                                <TweetImage src={tweet1} alt="Tweet 1" />
                                <TweetImage src={tweet2} alt="Tweet 2" />
                            </TweetSection>
                        </Container>
                    </GradientWrapper>
                    <Quote>
                        {documentToReactComponents(content.quote)}
                    </Quote>
                    <StatsSection>
                        <StatsTitle>
                            {content.fifthTitle}
                        </StatsTitle>
                        <StatsDescription>
                            {documentToReactComponents(content.fifthDescription, renderOptions)}
                        </StatsDescription>
                        <StatsContainer>
                            <StatItem>
                                <img src={impressionsIcon} alt="Impressions Icon" />
                                <p>100,000 interventions by the end of 2025</p>
                            </StatItem>
                            <StatItem>
                                <img src={platformsIcon} alt="Platforms Icon" />
                                <p>Deployment across 5+ platforms by 2027</p>
                            </StatItem>
                            <StatItem>
                                <img src={costIcon} alt="Cost Icon" />
                                <p>Lower cost per impression than most sponsored content</p>
                            </StatItem>
                            <StatItem>
                                <img src={usersIcon} alt="Users Icon" />
                                <p>Scales through individual users and partner organizations</p>
                            </StatItem>
                        </StatsContainer>
                    </StatsSection>
                    <SectionWrapper>
                        <CardWrapper>
                            <InfoCard>
                                <CardTitle>
                                    {content.contactTitle}
                                </CardTitle>
                                <Text>
                                    {documentToReactComponents(content.contactDescription, renderOptions)}
                                </Text>
                                <CardButton href={`mailto:info@civichealthproject.org?subject=${encodeURIComponent('Sending an inquiry about Normsy')}`}>
                                    Contact Us
                                </CardButton>
                            </InfoCard>
                        </CardWrapper>
                    </SectionWrapper>
                </>
            )}
            <Footer />
        </LandingWrapper>
    );
};

export default LandingPage;
